import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DeviceMobile, Desktop, Moon, Sun } from '@phosphor-icons/react';
import { useState, type ReactNode } from 'react';
import { useForm, useWatch, type Control } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { changePasswordInput, emailSchema, updateProfileInput } from '@brechoflow/contracts';
import { z } from 'zod';
import { useConfirm } from '@/components/confirm-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { toast } from '@/components/ui/toaster';
import { ME_KEY, useSession } from '@/features/auth/session';
import { ErrorPanel, FormError, ListSkeleton } from '@/features/settings/components';
import { isApiError, errorMessage } from '@/lib/api';
import { cn } from '@/lib/cn';
import { applyServerErrors, contractResolver } from '@/lib/forms';
import { formatDateTime } from '@/lib/format';
import { useTheme, type Theme } from '@/lib/theme';
import { accountApi, accountKeys } from './account-api';
import { passwordStrength } from './password-strength';
import { relativeTime, summarizeUserAgent } from './user-agent';

/** Bloco de uma seção da página: título, descrição e conteúdo. */
export function Section({ id, title, description, children, className }: { id: string; title: string; description?: string; children: ReactNode; className?: string }) {
  return (
    <section aria-labelledby={`${id}-title`} className={cn('min-w-0 rounded-panel border bg-card p-5 sm:p-6', className)}>
      <h2 id={`${id}-title`} className="font-display text-lg font-semibold">
        {title}
      </h2>
      {description && <p className="mt-1 max-w-prose text-sm text-muted-foreground">{description}</p>}
      <div className="mt-5">{children}</div>
    </section>
  );
}

function ReadOnlyRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5 sm:flex-row sm:items-baseline sm:gap-4">
      <dt className="shrink-0 text-sm text-muted-foreground sm:w-32">{label}</dt>
      <dd className="min-w-0 text-sm font-medium break-words">{value}</dd>
    </div>
  );
}

/* ----------------------------------------------------------- Dados de acesso */

const accessForm = updateProfileInput.extend({ email: emailSchema, currentPassword: z.string().max(128) });
type AccessValues = z.input<typeof accessForm>;

/** Nome e e-mail juntos; trocar o e-mail exige a senha atual. */
export function AccessSection({ operator = false }: { operator?: boolean }) {
  const { me, membership, setMe } = useSession();
  const qc = useQueryClient();
  const [formError, setFormError] = useState<string | null>(null);
  const current = { name: me?.user.name ?? '', email: me?.user.email ?? '' };
  const {
    register,
    handleSubmit,
    setError,
    reset,
    control,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<AccessValues, unknown, z.output<typeof accessForm>>({
    resolver: contractResolver(accessForm),
    defaultValues: { ...current, currentPassword: '' },
  });
  const email = useWatch({ control, name: 'email' }) ?? '';
  const emailChanged = email.trim().toLowerCase() !== current.email.toLowerCase();

  const onSubmit = handleSubmit(async (v) => {
    setFormError(null);
    const nameChanged = v.name !== current.name;
    const mailChanged = v.email.toLowerCase() !== current.email.toLowerCase();
    if (mailChanged && !v.currentPassword) {
      setError('currentPassword', { type: 'validate', message: 'Informe a senha atual para trocar o e-mail' });
      return;
    }
    try {
      let next = null;
      if (mailChanged) next = await accountApi.changeEmail({ currentPassword: v.currentPassword, newEmail: v.email });
      if (nameChanged) next = await accountApi.updateProfile({ name: v.name });
      if (next) {
        setMe(next);
        reset({ name: next.user.name, email: next.user.email, currentPassword: '' });
      }
      if (mailChanged) void qc.invalidateQueries({ queryKey: accountKeys.sessions });
      toast.success(mailChanged ? 'Dados salvos. Os outros dispositivos foram desconectados.' : 'Dados salvos');
    } catch (e) {
      if (isApiError(e) && e.code === 'invalid_password') setError('currentPassword', { type: 'server', message: e.message });
      else if (isApiError(e) && (e.code === 'email_unavailable' || e.code === 'email_unchanged')) setError('email', { type: 'server', message: e.message });
      else setFormError(applyServerErrors(e, setError));
    }
  });

  return (
    <Section id="acesso" title="Dados de acesso" description="Nome e e-mail usados para entrar e para receber avisos de segurança.">
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <FormError message={formError} />
        <FormField label="Nome" error={errors.name?.message}>
          {(a) => <Input autoComplete="name" maxLength={120} {...a} {...register('name')} />}
        </FormField>
        <FormField label="E-mail" error={errors.email?.message} hint={emailChanged || operator ? undefined : 'Avisaremos o e-mail anterior se você trocar.'}>
          {(a) => <Input type="email" autoComplete="email" inputMode="email" {...a} {...register('email')} />}
        </FormField>
        {emailChanged && (
          <FormField label="Senha atual" error={errors.currentPassword?.message} hint="Necessária para trocar o e-mail. Os outros dispositivos serão desconectados.">
            {(a) => <PasswordInput autoComplete="current-password" {...a} {...register('currentPassword')} />}
          </FormField>
        )}
        <div>
          <Button type="submit" loading={isSubmitting} disabled={!isDirty}>
            Salvar dados
          </Button>
        </div>
      </form>
      {!operator && (
        <dl className="mt-6 flex flex-col gap-3 border-t pt-5">
          <ReadOnlyRow label="Loja ativa" value={membership?.organizationName ?? 'Nenhuma'} />
          <ReadOnlyRow label="Seu papel" value={membership?.roleName ?? '-'} />
        </dl>
      )}
    </Section>
  );
}

/* ------------------------------------------------------------------- Senha */

const passwordForm = changePasswordInput
  .extend({ confirm: z.string().min(1, 'Repita a nova senha') })
  .refine((v) => v.newPassword === v.confirm, { path: ['confirm'], message: 'As senhas não conferem' })
  .refine((v) => v.newPassword !== v.currentPassword, { path: ['newPassword'], message: 'A nova senha deve ser diferente da atual' });
type PasswordValues = z.input<typeof passwordForm>;

function StrengthMeter({ control }: { control: Control<PasswordValues> }) {
  const pw = useWatch({ control, name: 'newPassword' }) ?? '';
  const s = passwordStrength(pw);
  const color = s.score === 3 ? 'bg-success' : s.score === 2 ? 'bg-warning' : 'bg-destructive';
  return (
    <div className="flex items-center gap-3" aria-live="polite">
      <div className="flex flex-1 gap-1" aria-hidden>
        {[1, 2, 3].map((i) => (
          <span key={i} className={cn('h-1.5 flex-1 rounded-full bg-muted', s.score >= i && color)} />
        ))}
      </div>
      <span className="w-24 text-xs text-muted-foreground">{pw ? `Senha ${s.label.toLowerCase()}` : ''}</span>
    </div>
  );
}

export function PasswordSection() {
  const qc = useQueryClient();
  const [formError, setFormError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const {
    register,
    handleSubmit,
    setError,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<PasswordValues, unknown, z.output<typeof passwordForm>>({
    resolver: contractResolver(passwordForm),
    defaultValues: { currentPassword: '', newPassword: '', confirm: '' },
  });

  const onSubmit = handleSubmit(async ({ currentPassword, newPassword }) => {
    setFormError(null);
    setDone(false);
    try {
      await accountApi.changePassword({ currentPassword, newPassword });
      reset();
      setDone(true);
      void qc.invalidateQueries({ queryKey: accountKeys.sessions });
      // a sessão (/auth/me) carrega `mustChangePassword`: recarrega para liberar o resto do app
      void qc.invalidateQueries({ queryKey: ME_KEY });
      toast.success('Senha alterada');
    } catch (e) {
      if (isApiError(e) && e.code === 'invalid_password') setError('currentPassword', { type: 'server', message: e.message });
      else setFormError(applyServerErrors(e, setError));
    }
  });

  return (
    <Section id="senha" title="Senha" description="Ao trocar a senha, os outros dispositivos são desconectados.">
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <FormError message={formError} />
        {done && (
          <p role="status" className="rounded-control bg-success-soft px-3 py-2.5 text-sm text-success">
            Senha alterada. Os outros dispositivos foram desconectados; esta sessão continua ativa.
          </p>
        )}
        <FormField label="Senha atual" error={errors.currentPassword?.message}>
          {(a) => <PasswordInput autoComplete="current-password" {...a} {...register('currentPassword')} />}
        </FormField>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex min-w-0 flex-col gap-2">
            <FormField label="Nova senha" hint="Mínimo de 8 caracteres, com letra e número." error={errors.newPassword?.message}>
              {(a) => <PasswordInput autoComplete="new-password" {...a} {...register('newPassword')} />}
            </FormField>
            <StrengthMeter control={control} />
          </div>
          <FormField label="Repita a nova senha" error={errors.confirm?.message}>
            {(a) => <PasswordInput autoComplete="new-password" {...a} {...register('confirm')} />}
          </FormField>
        </div>
        <div>
          <Button type="submit" loading={isSubmitting}>
            Alterar senha
          </Button>
        </div>
      </form>
    </Section>
  );
}

/* ----------------------------------------------------------------- Sessões */

export function SessionsSection() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { logout } = useSession();
  const q = useQuery({ queryKey: accountKeys.sessions, queryFn: accountApi.sessions });
  const refresh = () => qc.invalidateQueries({ queryKey: accountKeys.sessions });

  const revoke = useMutation({
    mutationFn: (id: string) => accountApi.revokeSession(id),
    onSuccess: () => {
      toast.success('Sessão encerrada');
      void refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const revokeOthers = useMutation({
    mutationFn: () => accountApi.revokeOthers(),
    onSuccess: () => {
      toast.success('Os outros dispositivos foram desconectados');
      void refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const sessions = q.data ?? [];
  const others = sessions.filter((s) => !s.current);

  const onRevoke = async (id: string, label: string) => {
    const ok = await confirm({ title: 'Encerrar esta sessão?', description: `${label} será desconectado e precisará entrar de novo.`, confirmLabel: 'Encerrar sessão', destructive: true });
    if (ok) revoke.mutate(id);
  };
  const onRevokeOthers = async () => {
    const ok = await confirm({ title: 'Encerrar todas as outras sessões?', description: 'Todos os dispositivos, menos este, serão desconectados.', confirmLabel: 'Encerrar todas', destructive: true });
    if (ok) revokeOthers.mutate();
  };
  const onLogoutCurrent = async () => {
    const ok = await confirm({ title: 'Encerrar a sessão atual?', description: 'Você sairá do sistema neste dispositivo.', confirmLabel: 'Sair agora', destructive: true });
    if (!ok) return;
    await logout();
    navigate('/entrar', { replace: true });
  };

  return (
    <Section id="sessoes" title="Sessões ativas" description="Dispositivos conectados à sua conta. Encerre os que você não reconhece.">
      {q.isPending ? (
        <ListSkeleton rows={2} />
      ) : q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <>
          <ul className="flex flex-col divide-y rounded-control border">
            {sessions.map((s) => {
              const d = summarizeUserAgent(s.userAgent);
              const Icon = d.mobile ? DeviceMobile : Desktop;
              return (
                <li key={s.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
                  <Icon className="size-6 shrink-0 text-muted-foreground" aria-hidden />
                  <div className="min-w-0 flex-1 basis-48">
                    <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
                      <span className="min-w-0 break-words">{d.label}</span>
                      {s.current && <Badge tone="primary">Este dispositivo</Badge>}
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      <span title={formatDateTime(s.lastUsedAt)}>Último uso {relativeTime(s.lastUsedAt)}</span>
                      {s.ip && <span> &middot; IP {s.ip}</span>}
                    </p>
                  </div>
                  {!s.current && (
                    <Button variant="secondary" size="sm" loading={revoke.isPending && revoke.variables === s.id} onClick={() => void onRevoke(s.id, d.label)} aria-label={`Encerrar sessão: ${d.label}`}>
                      Encerrar
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button variant="secondary" disabled={others.length === 0} loading={revokeOthers.isPending} onClick={() => void onRevokeOthers()}>
              Encerrar todas as outras
            </Button>
            <Button variant="secondary" onClick={() => void onLogoutCurrent()}>
              Encerrar esta sessão
            </Button>
          </div>
          {others.length === 0 && <p className="mt-3 text-sm text-muted-foreground">Nenhum outro dispositivo conectado.</p>}
        </>
      )}
    </Section>
  );
}

/* --------------------------------------------------------------- Aparência */

const THEMES: { value: Theme; label: string; icon: typeof Sun }[] = [
  { value: 'light', label: 'Claro', icon: Sun },
  { value: 'dark', label: 'Escuro', icon: Moon },
  { value: 'system', label: 'Do sistema', icon: Desktop },
];

export function AppearanceSection() {
  const { theme, setTheme } = useTheme();
  return (
    <Section id="aparencia" title="Aparência" description="Vale só para este navegador.">
      <div role="radiogroup" aria-label="Tema" className="grid grid-cols-3 gap-2">
        {THEMES.map((t) => {
          const active = theme === t.value;
          return (
            <button
              key={t.value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => setTheme(t.value)}
              className={cn(
                'flex min-h-16 min-w-0 flex-col items-center justify-center gap-1.5 rounded-control border px-2 py-3 text-sm font-medium transition-colors',
                active ? 'border-primary bg-primary/10 text-primary' : 'hover:bg-muted',
              )}
            >
              <t.icon className="size-5" weight={active ? 'fill' : 'regular'} aria-hidden />
              {t.label}
            </button>
          );
        })}
      </div>
    </Section>
  );
}
